import { useQuery } from '@tanstack/react-query';
import { 
  AlertTriangle, 
  DollarSign, 
  Snowflake,
  Thermometer,
  Building2 
} from 'lucide-react';
import { inventoryApi } from '../lib/api';
import PageHeader from '../components/PageHeader';

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  color: string;
}

function StatCard({ icon, label, value, subValue, color }: StatCardProps) {
  return (
    <div className="stats-card">
      <div className="icon" style={{ backgroundColor: `${color}20`, color }}>
        {icon}
      </div>
      <div className="value">{value}</div>
      <div className="label">{label}</div>
      {subValue && (
        <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
          {subValue}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { data: chilledData } = useQuery({
    queryKey: ['inventory', 'chilled'],
    queryFn: () => inventoryApi.getChilled({ hasStock: 'true' }),
  });

  const { data: frozenData } = useQuery({
    queryKey: ['inventory', 'frozen'],
    queryFn: () => inventoryApi.getFrozen({ hasStock: 'true' }),
  });

  const { data: byStorageData } = useQuery({
    queryKey: ['inventory', 'by-storage'],
    queryFn: () => inventoryApi.getByStorage(),
  });

  const { data: expiryData } = useQuery({
    queryKey: ['inventory', 'expiry-alert'],
    queryFn: () => inventoryApi.getExpiryAlert(7),
  });

  const chilledSummary = chilledData?.data?.summary || { totalQuantity: 0, totalWeight: 0, totalValue: 0 };
  const frozenSummary = frozenData?.data?.summary || { totalQuantity: 0, totalWeight: 0, totalValue: 0 };
  const totalValue = (Number(chilledSummary.totalValue) || 0) + (Number(frozenSummary.totalValue) || 0);
  const totalWeight = (Number(chilledSummary.totalWeight) || 0) + (Number(frozenSummary.totalWeight) || 0);
  const expiryCount = expiryData?.data?.total || 0;

  const formatNumber = (num: number) => num.toLocaleString('ja-JP');
  const formatCurrency = (num: number) => `¥${num.toLocaleString('ja-JP')}`;

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="ダッシュボード"
        description="在庫状況の概要を確認できます"
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<Thermometer size={24} />}
          label="チルド在庫"
          value={`${formatNumber(Number(chilledSummary.totalWeight) || 0)} kg`}
          subValue={`${formatNumber(Number(chilledSummary.totalQuantity) || 0)} ケース`}
          color="#06b6d4"
        />
        <StatCard
          icon={<Snowflake size={24} />}
          label="冷凍在庫"
          value={`${formatNumber(Number(frozenSummary.totalWeight) || 0)} kg`}
          subValue={`${formatNumber(Number(frozenSummary.totalQuantity) || 0)} ケース`}
          color="#8b5cf6"
        />
        <StatCard
          icon={<DollarSign size={24} />}
          label="在庫金額合計"
          value={formatCurrency(totalValue)}
          subValue={`総重量: ${formatNumber(totalWeight)} kg`}
          color="#22c55e"
        />
        <StatCard
          icon={<AlertTriangle size={24} />}
          label="期限アラート"
          value={expiryCount}
          subValue="7日以内に期限切れ"
          color={expiryCount > 0 ? "#f59e0b" : "#22c55e"}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Storage Summary */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            倉庫別在庫状況
          </h3>
          <div className="space-y-3">
            {byStorageData?.data?.data?.map((storage: any) => (
              <div
                key={storage.coldStorage.id}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: 'var(--light-gray-tertiary)' }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'var(--accent-color)' }}
                  >
                    <Building2 size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      {storage.coldStorage.name}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {formatNumber(storage.totalQuantity)} ケース
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {formatNumber(storage.totalWeight)} kg
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {formatCurrency(storage.totalValue)}
                  </p>
                </div>
              </div>
            )) || (
              <p className="text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                データがありません
              </p>
            )}
          </div>
        </div>

        {/* Expiry Alert */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            賞味期限アラート
          </h3>
          <div className="space-y-2">
            {expiryData?.data?.data?.slice(0, 5).map((item: any) => (
              <div
                key={item.inventory.id}
                className="flex items-center justify-between p-3 rounded-lg border"
                style={{ borderColor: 'var(--warning-color)', backgroundColor: 'rgba(245, 158, 11, 0.05)' }}
              >
                <div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                    {item.purchase.productName}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    LOT: {item.purchase.internalLot}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold" style={{ color: 'var(--warning-color)' }}>
                    {item.purchase.expiryDate}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    残: {item.inventory.currentQuantity} ケース
                  </p>
                </div>
              </div>
            )) || (
              <p className="text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                期限切れ間近の在庫はありません
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

